import { Icons } from '@tecalisdev/infinity';
import { WorkflowFormFieldType } from './form-field-types';

/** Variantes `tag-img` del design system (Figma `8857:31020`). */
export type FormFieldPresetIconStatus = 'default' | 'level' | 'control' | 'notice';

export interface IFormFieldPreset {
	id: string;
	icon: Icons;
	iconStatus: FormFieldPresetIconStatus;
	labelKey: string;
	type: WorkflowFormFieldType;
	fieldTitleKey: string;
	fieldName: string;
	fieldId: string;
	defaultQuestionKey: string;
	required?: boolean;
	/** Metadatos del campo (id, tipo, etiqueta) bloqueados; solo pauta/descripción editables (Figma datos personales). */
	fieldMetadataLocked?: boolean;
	/** Subcampos predefinidos del preset (Figma `9227-90850`). Si existe, sustituye al campo único `fieldId`. */
	presetFields?: IFormFieldPresetFieldDef[];
	/** Rellena `step_description` al aplicar el preset (Figma Nombre). El resto usa placeholder vacío. */
	prefillStepDescription?: boolean;
	/** Tipo mostrado en preview de presets genéricos (Figma `8979-243629`). */
	previewTypeLabelKey?: string;
	/** Rellena la pauta al aplicar (datos personales y selectores). */
	prefillGuideline?: boolean;
	/** Editor de opciones en Campos* (Figma `9275-21558`). */
	usesOptionsEditor?: boolean;
	/** Editor rich-text por opción (Figma checkbox `9275-21694`). */
	usesRichTextOptionsEditor?: boolean;
	/** Filas vacías al aplicar preset con editor de opciones. */
	defaultOptionsCount?: number;
	/** Preview fijo Sí/No en grid (Figma `9275-22022`). */
	usesYesNoPreview?: boolean;
	/** Sí/No preview + editor legal rich-text (Figma `9275-21762`). */
	usesLegalFieldsConfig?: boolean;
	/** Etiqueta del campo visible pero no obligatoria (Figma texto breve/largo). */
	fieldTitleOptional?: boolean;
}

export interface IFormFieldPresetFieldDef {
	titleKey: string;
	fieldName: string;
	fieldId: string;
	type: WorkflowFormFieldType;
	/** Clave i18n del tipo mostrado en `info-group` (Figma). */
	typeLabelKey: string;
	required?: boolean;
	/** Ancho en grid compuesto Figma (`9124-63783` Dirección). */
	gridSpan?: 'full' | 'half' | 'third';
}

/** Texto por defecto de Descripción en presets con cotejo (`step_description` del paso). */
export const FORM_FIELD_MATCH_OCR_DEFAULT_ALERT_KEY =
	'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.MATCH_OCR_DEFAULT_ALERT';

// ─── Política «Cotejar datos» (match_ocr → validators[] en API) ───────────────
//
// Presets del picker que muestran el switch en Configuración avanzada del modal.
// El envío al back no cambia: sigue siendo `validators: [..., 'match_ocr']` vía
// `WorkflowsHelperService.serializeFormField()` cuando el switch está activo.
//
// Negocio (NIF-208 / NIF-532): claves canónicas comparables en cotejo match_data.
// Para ampliar/restringir: editar FORM_FIELD_MATCH_OCR_FIELD_IDS y FORM_FIELD_MATCH_OCR_PRESET_IDS.

export const FORM_FIELD_MATCH_OCR_PRESET_IDS = ['name', 'id_document', 'birth_date', 'address'] as const;

export type FormFieldMatchOcrPresetId = (typeof FORM_FIELD_MATCH_OCR_PRESET_IDS)[number];

/** Subcampos de presets FORM que participan en cotejo match_data / validators `match_ocr`. */
export const FORM_FIELD_MATCH_OCR_FIELD_IDS = [
	'name',
	'surname',
	'surname2',
	'id_document_type',
	'id_document',
	'birth_date',
	'address',
	'address_number',
	'address_floor',
	'address_door',
	'zip_code',
	'city',
	'country',
	'province',
] as const;

const FORM_FIELD_MATCH_OCR_FIELD_ID_SET = new Set<string>(FORM_FIELD_MATCH_OCR_FIELD_IDS);

/** @deprecated Usar isFormFieldMatchOcrTargetField; conservado solo por compatibilidad de imports externos. */
export const FORM_FIELD_MATCH_OCR_TARGET_FIELD_ID: Readonly<Record<FormFieldMatchOcrPresetId, string>> = {
	name: 'name',
	id_document: 'id_document',
	birth_date: 'birth_date',
	address: 'address',
};

/** Activa `match_ocr` en los subcampos elegibles al aplicar un preset (el usuario puede desactivarlo). */
export const FORM_FIELD_MATCH_OCR_DEFAULT_ON_APPLY = true;

export function isFormFieldPresetMatchOcrEligible(
	presetId: string | null | undefined,
): presetId is FormFieldMatchOcrPresetId {
	return FORM_FIELD_MATCH_OCR_PRESET_IDS.includes(presetId as FormFieldMatchOcrPresetId);
}

function listPresetFieldIds(preset: IFormFieldPreset): string[] {
	if (preset.presetFields?.length) {
		return preset.presetFields.map((fieldDef) => fieldDef.fieldId);
	}

	return [preset.fieldId];
}

export function isFormFieldMatchOcrTargetField(presetId: string | null | undefined, fieldId: string): boolean {
	if (!isFormFieldPresetMatchOcrEligible(presetId) || !FORM_FIELD_MATCH_OCR_FIELD_ID_SET.has(fieldId)) {
		return false;
	}

	const preset = findFormFieldPreset(presetId);
	if (!preset) {
		return false;
	}

	return listPresetFieldIds(preset).includes(fieldId);
}

export function resolveFormFieldMatchOcrDefaultForField(presetId: string | null | undefined, fieldId: string): boolean {
	if (!FORM_FIELD_MATCH_OCR_DEFAULT_ON_APPLY) return false;
	return isFormFieldMatchOcrTargetField(presetId, fieldId);
}

export const FORM_FIELD_PRESETS: IFormFieldPreset[] = [
	{
		id: 'name',
		icon: 'icon-user' as Icons,
		iconStatus: 'default',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NAME',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NAME_FIELD',
		fieldName: 'name',
		fieldId: 'name',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NAME_QUESTION',
		required: true,
		fieldMetadataLocked: true,
		prefillStepDescription: true,
		// Figma: Nombre → Apellido.
		presetFields: [
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NAME_FIELD',
				fieldName: 'name',
				fieldId: 'name',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SURNAME_FIELD',
				fieldName: 'surname',
				fieldId: 'surname',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
			},
		],
	},
	{
		id: 'id_document',
		icon: 'icon-card' as Icons,
		iconStatus: 'default',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT_FIELD',
		fieldName: 'id_document',
		fieldId: 'id_document',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT_QUESTION',
		required: true,
		fieldMetadataLocked: true,
		presetFields: [
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT_TYPE',
				fieldName: 'id_document_type',
				fieldId: 'id_document_type',
				type: 'select',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
				required: true,
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT_FIELD',
				fieldName: 'id_document',
				fieldId: 'id_document',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
			},
		],
	},
	{
		id: 'birth_date',
		icon: 'icon-calendar' as Icons,
		iconStatus: 'default',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_BIRTH_DATE',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_BIRTH_DATE_FIELD',
		fieldName: 'birth_date',
		fieldId: 'birth_date',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_BIRTH_DATE_QUESTION',
		required: true,
		fieldMetadataLocked: true,
		presetFields: [
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_BIRTH_DATE_FIELD',
				fieldName: 'birth_date',
				fieldId: 'birth_date',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_DATE',
				required: true,
			},
		],
	},
	{
		id: 'address',
		icon: 'icon-map-pin' as Icons,
		iconStatus: 'default',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_FIELD',
		fieldName: 'address',
		fieldId: 'address',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_QUESTION',
		required: true,
		fieldMetadataLocked: true,
		presetFields: [
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_FIELD',
				fieldName: 'address',
				fieldId: 'address',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
				gridSpan: 'full',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_NUMBER',
				fieldName: 'address_number',
				fieldId: 'address_number',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
				gridSpan: 'third',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_FLOOR',
				fieldName: 'address_floor',
				fieldId: 'address_floor',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				gridSpan: 'third',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_DOOR',
				fieldName: 'address_door',
				fieldId: 'address_door',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				gridSpan: 'third',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_ZIP_CODE',
				fieldName: 'zip_code',
				fieldId: 'zip_code',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_NUMBER',
				required: true,
				gridSpan: 'half',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_CITY',
				fieldName: 'city',
				fieldId: 'city',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
				gridSpan: 'half',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_COUNTRY',
				fieldName: 'country',
				fieldId: 'country',
				type: 'select',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
				required: true,
				gridSpan: 'half',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_PROVINCE',
				fieldName: 'province',
				fieldId: 'province',
				type: 'select',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
				required: true,
				gridSpan: 'half',
			},
		],
	},
	{
		id: 'email',
		icon: 'icon-mail' as Icons,
		iconStatus: 'default',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_EMAIL',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_EMAIL_FIELD',
		fieldName: 'email',
		fieldId: 'email',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_EMAIL_QUESTION',
		required: true,
		fieldMetadataLocked: true,
		presetFields: [
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_EMAIL_FIELD',
				fieldName: 'email',
				fieldId: 'email',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
				required: true,
			},
		],
	},
	{
		id: 'phone',
		icon: 'icon-phone' as Icons,
		iconStatus: 'default',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE_FIELD',
		fieldName: 'phone',
		fieldId: 'phone',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE_QUESTION',
		required: true,
		fieldMetadataLocked: true,
		presetFields: [
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE_PREFIX',
				fieldName: 'phone_prefix',
				fieldId: 'phone_prefix',
				type: 'select',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
			},
			{
				titleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE_NUMBER',
				fieldName: 'phone',
				fieldId: 'phone',
				type: 'text',
				typeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_NUMBER',
				required: true,
			},
		],
	},
	{
		id: 'short_text',
		icon: 'icon-text' as Icons,
		iconStatus: 'level',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SHORT_TEXT',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SHORT_TEXT_FIELD',
		fieldName: 'short_text',
		fieldId: 'short_text',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SHORT_TEXT_QUESTION',
		required: true,
		fieldTitleOptional: true,
		previewTypeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
	},
	{
		id: 'long_text',
		icon: 'icon-align-left' as Icons,
		iconStatus: 'level',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LONG_TEXT',
		type: 'textarea',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LONG_TEXT_FIELD',
		fieldName: 'long_text',
		fieldId: 'long_text',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LONG_TEXT_QUESTION',
		required: true,
		fieldTitleOptional: true,
		previewTypeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_LONG_TEXT',
	},
	{
		id: 'multi_select',
		icon: 'icon-list-ticks' as Icons,
		iconStatus: 'control',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_MULTI_SELECT',
		type: 'checkbox',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_MULTI_SELECT_FIELD',
		fieldName: 'multi_select',
		fieldId: 'multi_select',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_MULTI_SELECT_QUESTION',
		required: true,
		prefillGuideline: true,
		usesOptionsEditor: true,
		defaultOptionsCount: 2,
	},
	{
		id: 'single_select',
		icon: 'icon-chevron-down' as Icons,
		iconStatus: 'control',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SINGLE_SELECT',
		type: 'select',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SINGLE_SELECT_FIELD',
		fieldName: 'single_select',
		fieldId: 'single_select',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SINGLE_SELECT_QUESTION',
		required: true,
		prefillGuideline: true,
		usesOptionsEditor: true,
		defaultOptionsCount: 2,
	},
	{
		id: 'checkbox',
		icon: 'icon-check' as Icons,
		iconStatus: 'control',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_CHECKBOX',
		type: 'checkbox',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_CHECKBOX_FIELD',
		fieldName: 'checkbox',
		fieldId: 'checkbox',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_CHECKBOX_QUESTION',
		required: true,
		usesOptionsEditor: true,
		usesRichTextOptionsEditor: true,
		defaultOptionsCount: 1,
	},
	{
		id: 'yes_no',
		icon: 'icon-x-circle' as Icons,
		iconStatus: 'control',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_YES_NO',
		type: 'radio',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_YES_NO_FIELD',
		fieldName: 'yes_no',
		fieldId: 'yes_no',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_YES_NO_QUESTION',
		required: true,
		usesYesNoPreview: true,
	},
	{
		id: 'legal',
		icon: 'icon-legal' as Icons,
		iconStatus: 'control',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LEGAL',
		type: 'checkbox',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LEGAL_FIELD',
		fieldName: 'legal',
		fieldId: 'legal',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LEGAL_QUESTION',
		required: true,
		usesLegalFieldsConfig: true,
		usesRichTextOptionsEditor: true,
		defaultOptionsCount: 1,
	},
	{
		id: 'date',
		icon: 'icon-calendar' as Icons,
		iconStatus: 'notice',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_DATE',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_DATE_FIELD',
		fieldName: 'date',
		fieldId: 'date',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_DATE_QUESTION',
		required: true,
		fieldTitleOptional: true,
		previewTypeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_DATE',
	},
	{
		id: 'number',
		icon: 'icon-fill-form' as Icons,
		iconStatus: 'notice',
		labelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NUMBER',
		type: 'text',
		fieldTitleKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NUMBER_FIELD',
		fieldName: 'number',
		fieldId: 'number',
		defaultQuestionKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NUMBER_QUESTION',
		required: true,
		fieldTitleOptional: true,
		previewTypeLabelKey: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_NUMBER',
	},
];

export function findFormFieldPreset(presetId: string | null | undefined): IFormFieldPreset | undefined {
	return FORM_FIELD_PRESETS.find((preset) => preset.id === presetId);
}

export function inferFormFieldPresetId(
	field: Record<string, unknown>,
	allFields: Record<string, unknown>[] = [],
): string {
	if (allFields.length) {
		const fieldIds = allFields
			.map((item) => String(item['id'] ?? item['name'] ?? ''))
			.filter(Boolean)
			.sort()
			.join(',');

		const byFields = FORM_FIELD_PRESETS.find((preset) => {
			if (!preset.presetFields?.length) {
				return false;
			}
			const presetIds = preset.presetFields
				.map((item) => item.fieldId)
				.sort()
				.join(',');
			return presetIds === fieldIds;
		});
		if (byFields) {
			return byFields.id;
		}
	}

	const fieldId = String(field['id'] ?? field['name'] ?? '');
	const byId = FORM_FIELD_PRESETS.find(
		(preset) =>
			preset.id === fieldId ||
			preset.fieldId === fieldId ||
			preset.presetFields?.some((item) => item.fieldId === fieldId),
	);
	if (byId) {
		return byId.id;
	}

	const type = String(field['type'] ?? 'text');
	const fallbackByType: Record<string, string> = {
		textarea: 'long_text',
		select: 'single_select',
		checkbox: 'checkbox',
		radio: 'single_select',
		file: 'short_text',
		text: 'short_text',
	};
	return fallbackByType[type] ?? 'short_text';
}
